import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { getSignedFilesUrl } from '../../utils/signedUrls';
import { useReportFilters } from '../../hooks/useReportFilters';
import ReportsFilterBar from '../../components/reports/ReportsFilterBar';
import ReportCameraButton from '../../components/reports/ReportCameraButton';
import ReportDownloadButton from '../../components/reports/ReportDownloadButton';
import {
  getMetadataColor,
  getMetadataBgColor,
  ORDERS_COLOR,
  getOrdersBgColor,
} from '../../utils/metadataColors';
import { isPatientUpload, patientUploadLabel } from '../../utils/uploadProvenance';
import {
  bucketName_files,
  BASE_URLS,
  allowedFileTypes,
  file_reference_column,
  colid2,
  prompts,
} from '../../components/env';
import { chartTable } from './chartSource';

// ─── filename sanitiser (mirrors Uploader.js) ────────────────────────────────
function sanitizeFilename(filename) {
  if (!filename) return 'file.png';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return (filename.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_') || 'file');
  const name = filename.substring(0, lastDot)
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'file';
  const ext = filename.substring(lastDot + 1).toLowerCase();
  return `${name}.${ext}`;
}

// Constructs the report folder path the way Uploader.js does so the medstudent
// page writes files into the same place the doctor flow expects to find them.
function buildReportsFolder(patient) {
  if (!patient) return null;
  const ehrIdFieldName = prompts.find(p => p[0] === 'EHR_ID')?.[0] || 'EHR_ID';
  let ehrId = patient[ehrIdFieldName] || patient.EHR_ID || patient.ehr_id || '';
  if (ehrId && typeof ehrId === 'string' && ehrId.includes('@')) ehrId = '';
  const folderName = ehrId ? `${patient[colid2]}_${ehrId}` : `${patient[colid2]}_`;
  const dateFolder = patient.date_
    ? patient.date_.split('-').slice(1).join('-') + '-' + patient.date_.slice(2, 4)
    : '';
  const schema = supabaseSchema && supabaseSchema !== 'public' ? `${supabaseSchema}/` : '';
  const path = `New/Reports/${schema}${folderName}/${dateFolder}`;
  return path.replace(/\/+$/, '');
}

function backendUrl() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  return hostname === 'localhost' ? BASE_URLS.local.backend : BASE_URLS.render.backend;
}

// ─── draggable + resizable container helpers ─────────────────────────────────
// Initial sizing — centred in viewport. Keep these bounded so the modal never
// renders fully off-screen on small windows.
function defaultPanel() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const width = Math.min(640, Math.round(vw * 0.7));
  const height = Math.min(560, Math.round(vh * 0.75));
  return {
    x: Math.max(16, Math.round((vw - width) / 2)),
    y: Math.max(16, Math.round((vh - height) / 2)),
    width,
    height,
  };
}

export default function MedStudentReportsModal({ isOpen, onClose, patient, readOnly = false }) {
  const [files, setFiles] = useState([]);
  const [fileMeta, setFileMeta] = useState({}); // { [filePath]: { classification, metadata, full_text, is_faxed_report, created_at, uploaded_by_name } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // {filename, stage}
  const fileInputRef = useRef(null);

  // Cross-schema (linked-provider) patients carry their home schema on the
  // patient object; falsy => byte-identical to pre-Task-6 behavior (plain client).
  const pschema = patient?._schema;

  // patient.reports stores the existing folder if any; otherwise we compute it
  // on demand so uploads go to the canonical location even for new patients.
  const reportsPath = patient?.reports || null;
  const computedFolder = useMemo(() => buildReportsFolder(patient), [patient]);
  const listingPath = reportsPath || computedFolder;

  // ── load reports list + classify_cache metadata when modal opens or after upload ─
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError(null);
    if (!listingPath) {
      setFiles([]);
      setFileMeta({});
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const sb = await getSupabase();
        // Storage calls are path-driven and stay on the raw client (schema()
        // scopes Postgrest only — it has no `.storage`).
        const { data, error: listErr } = await sb.storage
          .from(bucketName_files || 'files')
          .list(listingPath);
        if (cancelled) return;
        if (listErr) throw listErr;
        const onlyFiles = (data || []).filter(d => d.name && d.name.includes('.'));
        setFiles(onlyFiles);

        // Hydrate metadata from file_classification_cache so this modal shows
        // the same OCR/AI summary/classification chips as the doctor's view-reports modal.
        if (onlyFiles.length > 0) {
          const filePaths = onlyFiles.map(f => `${listingPath}/${f.name}`);
          const client = pschema ? sb.schema(pschema) : sb;
          try {
            const { data: rows } = await client
              .from('file_classification_cache')
              .select('file_path, classification, is_order, metadata, full_text, is_faxed_report, created_at, uploaded_by_name')
              .in('file_path', filePaths);
            if (!cancelled && rows) {
              const map = {};
              for (const r of rows) map[r.file_path] = r;
              setFileMeta(map);
            }
          } catch (metaErr) {
            // Public schema may not have the table — leave fileMeta empty rather than failing the listing.
            console.warn('[MedStudentReports] file_classification_cache fetch failed:', metaErr?.message);
            setFileMeta({});
          }
        } else {
          setFileMeta({});
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, listingPath, refreshTick, pschema]);

  // ── drag + resize ──────────────────────────────────────────────────────────
  const [panel, setPanel] = useState(defaultPanel);
  const dragRef = useRef(null); // { offsetX, offsetY } when dragging from header
  const resizeRef = useRef(null); // { startX, startY, startW, startH }

  // Re-centre when reopened (so user can recover from "dragged off-screen").
  useEffect(() => { if (isOpen) setPanel(defaultPanel()); }, [isOpen]);

  const onHeaderMouseDown = useCallback((e) => {
    // Ignore clicks on the close button etc.
    if (e.target.closest('button, input, a')) return;
    dragRef.current = {
      offsetX: e.clientX - panel.x,
      offsetY: e.clientY - panel.y,
    };
    e.preventDefault();
  }, [panel.x, panel.y]);

  // `side` is one of n / s / e / w / nw / ne / sw / se. The handler stores the
  // starting mouse + panel rect so move math anchors the opposite edge: e.g.
  // dragging the NW corner shrinks/grows toward the SE corner.
  const MIN_W = 360;
  const MIN_H = 280;
  const onResizeMouseDown = useCallback((side) => (e) => {
    resizeRef.current = {
      side,
      startX: e.clientX,
      startY: e.clientY,
      startW: panel.width,
      startH: panel.height,
      startX0: panel.x,
      startY0: panel.y,
    };
    e.preventDefault();
    e.stopPropagation();
  }, [panel]);

  useEffect(() => {
    if (!isOpen) return;
    const onMove = (e) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (dragRef.current) {
        const nx = Math.max(0, Math.min(vw - 80, e.clientX - dragRef.current.offsetX));
        const ny = Math.max(0, Math.min(vh - 40, e.clientY - dragRef.current.offsetY));
        setPanel(p => ({ ...p, x: nx, y: ny }));
      } else if (resizeRef.current) {
        const r = resizeRef.current;
        const dx = e.clientX - r.startX;
        const dy = e.clientY - r.startY;
        let newW = r.startW;
        let newH = r.startH;
        let newX = r.startX0;
        let newY = r.startY0;
        if (r.side.includes('w')) {
          // Anchor the right edge: shrink/grow toward the left.
          const target = r.startW - dx;
          newW = Math.max(MIN_W, Math.min(r.startX0 + r.startW, target));
          newX = (r.startX0 + r.startW) - newW;
        } else if (r.side.includes('e')) {
          // Anchor the left edge: shrink/grow toward the right.
          const target = r.startW + dx;
          newW = Math.max(MIN_W, Math.min(vw - r.startX0 - 8, target));
          newX = r.startX0;
        }
        if (r.side.includes('n')) {
          // Anchor the bottom edge: shrink/grow toward the top.
          const target = r.startH - dy;
          newH = Math.max(MIN_H, Math.min(r.startY0 + r.startH, target));
          newY = (r.startY0 + r.startH) - newH;
        } else if (r.side.includes('s')) {
          // Anchor the top edge: shrink/grow toward the bottom.
          const target = r.startH + dy;
          newH = Math.max(MIN_H, Math.min(vh - r.startY0 - 8, target));
          newY = r.startY0;
        }
        setPanel({ x: newX, y: newY, width: newW, height: newH });
      }
    };
    const onUp = () => { dragRef.current = null; resizeRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isOpen]);

  // ── upload pipeline (mirrors Uploader.handleUpload + classify-file) ────────
  const handleFilePicked = useCallback(async (event) => {
    if (readOnly) return;
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;
    event.target.value = ''; // reset so the same file can be re-picked
    if (!computedFolder) {
      setError('Cannot upload — this patient has no schedule date so the report folder cannot be computed.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const sb = await getSupabase();
      // .auth lives only on the raw client — schema() scopes Postgrest only.
      const client = pschema ? sb.schema(pschema) : sb;
      const { data: sessionData } = await sb.auth.getSession();
      const userId = sessionData?.session?.user?.id || null;
      let modelIds = null;
      try {
        const saved = localStorage.getItem('aiAssistant_selectedModels');
        if (saved) modelIds = JSON.parse(saved);
      } catch { /* ignore */ }

      for (const file of selectedFiles) {
        const sanitized = sanitizeFilename(file.name);
        const filePath = `${computedFolder}/${sanitized}`;
        setUploadStatus({ filename: file.name, stage: 'Uploading…' });
        const ext = file.name.split('.').pop().toLowerCase();
        const contentType =
          ext === 'png' ? 'image/png' :
          ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' :
          ext === 'pdf' ? 'application/pdf' :
          ext === 'csv' ? 'text/csv' :
          ext === 'txt' ? 'text/plain' :
          ext === 'html' ? 'text/html' :
          ext === 'json' ? 'application/json' :
          file.type || 'application/octet-stream';
        const { error: upErr } = await sb.storage
          .from(bucketName_files || 'files')
          .upload(filePath, file, { contentType, upsert: false });
        if (upErr) throw upErr;

        // Mirror Uploader.js: always write the folder path into the row's `reports`
        // column so doctor + medstudent flows both see the same files (the doctor
        // flow updates unconditionally; we now do the same).
        if (patient?.id) {
          try {
            await client.from(chartTable(patient)).update({ [file_reference_column]: computedFolder }).eq('id', patient.id);
          } catch { /* leave folder write best-effort */ }
        }

        // Skip OCR/classification for JSON files (matches doctor flow).
        if (ext !== 'json') {
          setUploadStatus({ filename: file.name, stage: 'Extracting text + AI summary…' });
          try {
            const resp = await fetch(`${backendUrl()}/api/ai-assistant/classify-file/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                file_path: filePath,
                userId,
                modelIds,
                modelId: modelIds && modelIds.length > 0 ? modelIds[0] : null,
              }),
            });
            if (!resp.ok) {
              const txt = await resp.text();
              console.warn('[MedStudentReports] classify-file failed:', resp.status, txt);
            }
          } catch (classifyErr) {
            console.warn('[MedStudentReports] classify-file error:', classifyErr.message);
          }
        }
      }
      setRefreshTick(t => t + 1);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setUploading(false);
      setUploadStatus(null);
    }
  }, [computedFolder, patient, reportsPath, readOnly, pschema]);

  // ── classification filters ─────────────────────────────────────────────────
  // Called unconditionally in the component body: `if (!isOpen) return null`
  // below is an early return, so a hook placed after it would render fewer hooks
  // on a closed modal and break the rules of hooks.
  const filterEntries = useMemo(
    () => files.map((f) => {
      const meta = fileMeta[`${listingPath}/${f.name}`] || null;
      return { classification: meta?.classification ?? null, is_order: !!meta?.is_order };
    }),
    [files, fileMeta, listingPath]
  );
  const { activeFilter, setActiveFilter, counts, visibleFilters, matches } =
    useReportFilters(filterEntries);
  const visibleFiles = useMemo(
    () => files.filter((f) => {
      const meta = fileMeta[`${listingPath}/${f.name}`] || null;
      return matches({ classification: meta?.classification ?? null, is_order: !!meta?.is_order });
    }),
    [files, fileMeta, listingPath, matches]
  );

  if (!isOpen) return null;

  // Report links resolve to a short-lived signed URL on click (see the list
  // below) so they work whether `files` is a public or private bucket.
  const openReport = async (name) => {
    const url = await getSignedFilesUrl(`${listingPath}/${name}`);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    // Overlay no longer blocks page below — modal acts as a floating window.
    // Click outside the panel still closes (pointerdown on the overlay).
    <div className="fixed inset-0 z-[60] pointer-events-none">
      <div
        role="dialog"
        aria-label="Reports"
        className="absolute pointer-events-auto bg-white dark:bg-[#14141c] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800/60 flex flex-col overflow-hidden"
        style={{
          left: panel.x,
          top: panel.y,
          width: panel.width,
          height: panel.height,
          minWidth: 360,
          minHeight: 280,
        }}
      >
        {/* Header — drag handle */}
        <div
          onMouseDown={onHeaderMouseDown}
          className="px-5 py-4 border-b border-slate-200 dark:border-slate-800/60 flex items-center justify-between cursor-move select-none"
          title="Drag to move"
        >
          <div>
            <div className="font-extrabold text-base text-slate-900 dark:text-slate-50">Reports</div>
            <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">
              {patient?.patient_name || 'Patient'}
              {listingPath ? ` · ${listingPath}` : ' · no reports folder set'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept={(allowedFileTypes || []).map(t => `.${t}`).join(',')}
                  onChange={handleFilePicked}
                />
                <button
                  type="button"
                  disabled={uploading || !computedFolder}
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                >
                  {uploading ? 'Uploading…' : '+ Upload'}
                </button>
                {/* Reuses the upload pipeline verbatim via a synthetic change event —
                    handleFilePicked only reads target.files and writes target.value.
                    The component stops click propagation itself (button AND hidden
                    input) so the header drag handle is unaffected. */}
                <ReportCameraButton
                  disabled={uploading || !computedFolder}
                  onCapture={(file) => handleFilePicked({ target: { files: [file], value: '' } })}
                />
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {uploadStatus && (
            <div className="mb-3 text-xs font-semibold text-blue-800 dark:text-blue-200 bg-blue-100 dark:bg-blue-500/15 rounded-md px-3 py-2">
              {uploadStatus.filename} — {uploadStatus.stage}
            </div>
          )}
          {!listingPath && (
            <div className="text-sm text-slate-700 dark:text-slate-300 py-6 text-center">
              This patient has no reports folder configured yet.
            </div>
          )}
          {listingPath && loading && (
            <div className="text-sm text-slate-700 dark:text-slate-300 py-6 text-center">Loading…</div>
          )}
          {listingPath && error && (
            <div className="text-sm text-red-700 dark:text-red-300 py-6 text-center">{error}</div>
          )}
          {listingPath && !loading && !error && files.length === 0 && (
            <div className="text-sm text-slate-700 dark:text-slate-300 py-6 text-center">
              No reports uploaded yet for this patient.
            </div>
          )}
          {!loading && files.length > 0 && (
            <ReportsFilterBar
              filters={visibleFilters}
              counts={counts}
              activeFilter={activeFilter}
              onSelect={setActiveFilter}
            />
          )}
          {!loading && files.length > 0 && visibleFiles.length === 0 && (
            <div className="text-sm text-slate-700 dark:text-slate-300 py-6 text-center">
              No reports match the “{activeFilter}” filter.
            </div>
          )}
          {!loading && visibleFiles.length > 0 && (
            <ul className="space-y-2">
              {visibleFiles.map(f => {
                const filePath = `${listingPath}/${f.name}`;
                const meta = fileMeta[filePath] || null;
                const classification = meta?.classification || null;
                const rawMd = meta?.metadata && typeof meta.metadata === 'object' ? meta.metadata : null;
                const headline = rawMd && (rawMd.document_type || rawMd.patient_name || rawMd.diagnosis);
                const summary = rawMd?.ai_summary || rawMd?.summary || null;
                const tooltipPairs = rawMd
                  ? Object.entries(rawMd)
                      .filter(([k, v]) => k !== 'truncation_info' && v != null && v !== '')
                      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.join(', ') : v}`)
                      .join(' · ')
                  : '';
                return (
                  <li key={f.name}>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); openReport(f.name); }}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                    >
                      <div className="w-8 h-8 rounded-md bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold shrink-0">
                        {(f.name.split('.').pop() || '?').slice(0, 4).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 truncate">{f.name}</div>
                        <div className="text-[11px] text-slate-700 dark:text-slate-300 flex items-center flex-wrap gap-x-2 gap-y-1 mt-0.5">
                          {f.metadata?.size != null && (
                            <span>{Math.round(f.metadata.size / 1024)} KB</span>
                          )}
                          {meta?.created_at && (
                            <span>· {new Date(meta.created_at).toLocaleDateString()}</span>
                          )}
                          {/* A patient-uploaded report gets its own badge
                              rather than the plain "Uploaded by X" line — the
                              clinician needs to see at a glance that this came
                              from the patient, not from staff or a fax. */}
                          {isPatientUpload(meta?.metadata, meta?.uploaded_by_name) ? (
                            <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40">
                              ⬆ {patientUploadLabel(meta?.metadata, meta?.uploaded_by_name)}
                            </span>
                          ) : (
                            meta?.uploaded_by_name && (
                              <span className="text-purple-800 dark:text-purple-200 font-semibold">
                                {meta.is_faxed_report ? 'Assigned' : 'Uploaded'} by {meta.uploaded_by_name}
                              </span>
                            )
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {/* Colour comes from the shared classification map — this chip
                              used to be hardcoded emerald for every category. */}
                          {classification && (
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded"
                              style={{
                                backgroundColor: getMetadataBgColor(classification),
                                color: getMetadataColor(classification),
                                border: `1px solid ${getMetadataColor(classification)}`,
                              }}
                            >
                              {classification}
                            </span>
                          )}
                          {meta?.is_order && (
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded"
                              style={{
                                backgroundColor: getOrdersBgColor(),
                                color: ORDERS_COLOR,
                                border: `1px solid ${ORDERS_COLOR}`,
                              }}
                              title={rawMd?.order_note || 'Flagged as an order'}
                            >
                              Order
                            </span>
                          )}
                          {headline && (
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-200"
                              title={tooltipPairs}
                            >
                              {headline}
                            </span>
                          )}
                          {meta?.full_text && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100">
                              Text extracted
                            </span>
                          )}
                        </div>
                        {/* Metadata box — listing every extracted field. Mirrors the data
                            shown in the doctor's view-reports modal (ResultsTable), but
                            visible inline rather than hidden in a hover tooltip. */}
                        {rawMd && (
                          <div className="mt-2 rounded-md border border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/40 px-3 py-2">
                            {summary && (
                              <div className="text-[12px] leading-snug text-slate-800 dark:text-slate-100 mb-2 whitespace-pre-line">
                                {summary}
                              </div>
                            )}
                            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px]">
                              {Object.entries(rawMd)
                                .filter(([k, v]) =>
                                  k !== 'truncation_info' &&
                                  k !== 'ai_summary' &&
                                  k !== 'summary' &&
                                  v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
                                )
                                .map(([k, v]) => (
                                  <React.Fragment key={k}>
                                    <dt className="font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                      {k.replace(/_/g, ' ')}
                                    </dt>
                                    <dd className="text-slate-800 dark:text-slate-100 break-words">
                                      {Array.isArray(v) ? v.join(', ') : String(v)}
                                    </dd>
                                  </React.Fragment>
                                ))}
                            </dl>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 mt-1">
                        {meta?.is_order && (
                          <ReportDownloadButton
                            filePath={filePath}
                            filename={f.name}
                            onError={(err) => setError(err?.message || String(err))}
                          />
                        )}
                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">Open ↗</span>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Edge resize handles — thin strips that stop short of the corners so
            the 16-px corner targets never get covered. */}
        <div onMouseDown={onResizeMouseDown('n')}  className="absolute top-0    left-4  right-4 h-1.5 cursor-ns-resize z-10" title="Resize" />
        <div onMouseDown={onResizeMouseDown('s')}  className="absolute bottom-0 left-4  right-4 h-1.5 cursor-ns-resize z-10" title="Resize" />
        <div onMouseDown={onResizeMouseDown('w')}  className="absolute left-0   top-4   bottom-4 w-1.5 cursor-ew-resize z-10" title="Resize" />
        <div onMouseDown={onResizeMouseDown('e')}  className="absolute right-0  top-4   bottom-4 w-1.5 cursor-ew-resize z-10" title="Resize" />
        {/* Corner resize handles — 16px clickable squares, rendered above edges. */}
        <div onMouseDown={onResizeMouseDown('nw')} className="absolute top-0    left-0   w-4 h-4 cursor-nwse-resize z-20" title="Resize" />
        <div onMouseDown={onResizeMouseDown('ne')} className="absolute top-0    right-0  w-4 h-4 cursor-nesw-resize z-20" title="Resize" />
        <div onMouseDown={onResizeMouseDown('sw')} className="absolute bottom-0 left-0   w-4 h-4 cursor-nesw-resize z-20" title="Resize" />
        <div
          onMouseDown={onResizeMouseDown('se')}
          title="Drag to resize"
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-20"
          style={{
            background:
              'linear-gradient(135deg, transparent 50%, rgba(100,116,139,0.55) 50%, rgba(100,116,139,0.55) 60%, transparent 60%, transparent 70%, rgba(100,116,139,0.55) 70%, rgba(100,116,139,0.55) 80%, transparent 80%)',
          }}
        />
      </div>
    </div>
  );
}
